import { PostgreSQL, sql } from '@codemirror/lang-sql'
import { EditorView } from '@codemirror/view'
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Stack, TextField, Typography } from '@mui/material'
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view'
import CodeMirror from '@uiw/react-codemirror'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { useAppSelector } from '../../store/Hooks'
import { snackbar } from '../services/SnackbarService'

const parentKey = 'on_demand'

interface QueryTreeNode {
    id: string
    name: string
    queryKey?: string
    children: QueryTreeNode[]
}

interface QueryEditorState {
    originalKey: string | null
    name: string
    content: string
}

const emptyEditor: QueryEditorState = {
    originalKey: null,
    name: '',
    content: ''
}

const normalizeQueryKey = (key: string) => key.replace(/\.json$/, '')

const handleError = (error: unknown, message: string) => {
    console.error(error)
    snackbar.show({
        severity: 'error',
        text: message
    })
}

const buildQueryTree = (queries: string[]): QueryTreeNode[] => {
    const root: QueryTreeNode = {
        id: 'root',
        name: 'root',
        children: []
    }

    for (const raw of queries) {
        const query = normalizeQueryKey(raw)
        const parts = query.split('/').filter(Boolean)

        let current = root

        parts.forEach((part, index) => {
            let child = current.children.find(
                c => c.name === part
            )

            if (!child) {
                child = {
                    id: parts.slice(0, index + 1).join('/'),
                    name: part,
                    children: []
                }
                current.children.push(child)
            }

            if (index === parts.length - 1) {
                child.queryKey = query
            }

            current = child
        })
    }

    return root.children
}

const renderNode = (node: QueryTreeNode, onSelect: (queryKey: string) => void): React.ReactNode => {
    const isLeaf = Boolean(node.queryKey)

    return (
        <TreeItem
            key={node.id}
            itemId={node.id}
            label={(
                <Typography
                    variant='body2'
                    sx={{ wordBreak: 'break-word' }}>
                    {node.name}
                </Typography>
            )}
            onClick={e => {
                e.stopPropagation()
                if (isLeaf && node.queryKey) {
                    onSelect(node.queryKey)
                }
            }}>
            {node.children.map(child =>
                renderNode(child, onSelect)
            )}
        </TreeItem>
    )
}

export default function QueryBuilderPage() {
    const mode = useAppSelector(state => state.app.mode)

    const [queries, setQueries] = useState<string[]>([])
    const [loadingQueries, setLoadingQueries] = useState(false)

    const [loadingContent, setLoadingContent] = useState(false)

    const [saving, setSaving] = useState(false)

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const [editor, setEditor] = useState<QueryEditorState>(emptyEditor)

    const savedSnapshot = useRef<QueryEditorState>(emptyEditor)

    const latestLoadRequest = useRef(0)

    const queryTree = useMemo(() => buildQueryTree(queries), [queries])

    const selectedQuery = editor.originalKey
    const queryName = editor.name
    const sqlQuery = editor.content

    const resetEditor = useCallback(() => {
        setEditor(emptyEditor)
    }, [])

    const loadQueries = useCallback(async () => {
        try {
            setLoadingQueries(true)

            const result = await ApiClient.getStoredQueries()

            const normalized = result.map(normalizeQueryKey)

            setQueries(normalized)
        } catch (error) {
            handleError(error, 'Failed to load queries')
        } finally {
            setLoadingQueries(false)
        }
    }, [])

    const loadQueryContent = useCallback(async (key: string) => {
        const requestId = ++latestLoadRequest.current

        try {
            setLoadingContent(true)

            const query = await ApiClient.getStoredQueryContent(key)

            if (requestId !== latestLoadRequest.current) {
                return
            }

            const nextEditor = {
                originalKey: query.key,
                name: query.key,
                content: query.content
            }

            setEditor(nextEditor)
            savedSnapshot.current = nextEditor
        } catch (error) {
            handleError(error, 'Failed to load query')
        } finally {
            if (requestId === latestLoadRequest.current) {
                setLoadingContent(false)
            }
        }
    }, [])

    const handleNew = useCallback(() => {
        resetEditor()
        savedSnapshot.current = emptyEditor
    }, [resetEditor])

    const handleSave = useCallback(async () => {
        if (!queryName.trim()) {
            snackbar.show({
                severity: 'error',
                text: 'Please enter a query name'
            })
            return
        }

        try {
            setSaving(true)

            await ApiClient.updateStoredQuery(queryName.trim(), sqlQuery)

            await loadQueries()

            setEditor(prev => ({
                ...prev,
                originalKey: queryName.trim()
            }))

            savedSnapshot.current = {
                originalKey: queryName.trim(),
                name: queryName.trim(),
                content: sqlQuery
            }

            snackbar.show({
                severity: 'success',
                text: 'Query saved successfully'
            })
        } catch (error) {
            handleError(error, 'Failed to save query')
        } finally {
            setSaving(false)
        }
    }, [queryName, sqlQuery, loadQueries])

    const handleDelete = useCallback(async () => {
        if (!selectedQuery) return

        setDeleteDialogOpen(false)

        try {
            await ApiClient.deleteStoredQuery(`${parentKey}/${selectedQuery}`)

            await loadQueries()

            resetEditor()

            savedSnapshot.current = emptyEditor

            snackbar.show({
                severity: 'success',
                text: 'Query deleted'
            })
        } catch (error) {
            handleError(error, 'Failed to delete query')
        }
    }, [selectedQuery, loadQueries, resetEditor])

    useEffect(() => {
        loadQueries()
    }, [loadQueries])

    return (
        <Stack
            sx={{
                height: '100%',
                minHeight: 0,
                p: 2,
                gap: 2,
                bgcolor: 'background.default',
                overflow: 'hidden'
            }}>
            {/* Toolbar */}
            <Paper sx={{ p: 2 }}>
                <Stack
                    direction='row'
                    spacing={2}
                    alignItems='center'>
                    <Button
                        variant='contained'
                        onClick={handleNew}>
                        New
                    </Button>

                    <Button
                        variant='contained'
                        color='success'
                        onClick={handleSave}
                        disabled={saving}>
                        {saving
                            ? (
                                    <CircularProgress
                                        size={18}
                                        color='inherit'
                                    />
                                )
                            : (
                                    'Save'
                                )}
                    </Button>

                    <Button
                        variant='outlined'
                        color='error'
                        disabled={!selectedQuery}
                        onClick={() =>
                            setDeleteDialogOpen(true)}>
                        Delete
                    </Button>

                    <TextField
                        label='Query Name'
                        value={queryName}
                        onChange={e => setEditor(prev => ({
                            ...prev,
                            name: e.target.value
                        }))}
                        size='small'
                        sx={{ minWidth: 500 }}
                    />
                </Stack>
            </Paper>

            <Stack
                sx={{
                    flex: 1,
                    flexDirection: 'row',
                    gap: 2,
                    minHeight: 0,
                    overflow: 'hidden'
                }}>
                {/* Tree */}
                <Paper
                    sx={{
                        width: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden'
                    }}>
                    <Typography sx={{ p: 2 }} variant='h6'>
                        Saved Queries
                    </Typography>

                    {loadingQueries
                        ? (
                                <Stack
                                    sx={{
                                        flex: 1,
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>
                                    <CircularProgress />
                                </Stack>
                            )
                        : (
                                <SimpleTreeView
                                    selectedItems={
                                        selectedQuery
                                            ? [`${parentKey}/${selectedQuery}`]
                                            : []
                                    }>
                                    {queryTree.map(node =>
                                        renderNode(
                                            node,
                                            loadQueryContent
                                        )
                                    )}
                                </SimpleTreeView>
                            )}
                </Paper>

                {/* Editor */}
                <Paper
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}>
                        <Typography variant='h6'>
                            SQL Editor
                        </Typography>
                    </Box>

                    {loadingContent
                        ? (
                                <Stack
                                    sx={{
                                        flex: 1,
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>
                                    <CircularProgress />
                                </Stack>
                            )
                        : (
                                <Stack
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: 'hidden',
                                        flexDirection: 'row'
                                    }}>
                                    <CodeMirror
                                        value={sqlQuery}
                                        extensions={[
                                            sql({ dialect: PostgreSQL }),
                                            EditorView.lineWrapping
                                        ]}
                                        onChange={value => setEditor(prev => ({
                                            ...prev,
                                            content: value
                                        }))}
                                        theme={mode === 'dark' ? 'dark' : 'light'}
                                        height='100%'
                                        style={{ flexGrow: 1 }}
                                    />
                                </Stack>
                            )}
                </Paper>
            </Stack>

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() =>
                    setDeleteDialogOpen(false)}>
                <DialogTitle>
                    Delete Query
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Delete &quot;{selectedQuery}&quot;?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>

                    <Button
                        color='error'
                        variant='contained'
                        onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}
